// import * as React from 'react';
import React, { useCallback, useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  Grid,
} from "@mui/material";
import PrintPDFLokasi from "./download_laporan_lokasi";
import DownloadIcon from "@mui/icons-material/Download";
import PreviewIcon from "@mui/icons-material/Preview";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import html2pdf from 'html2pdf.js';
import { useLocalStorage } from '../../auth/useLocalStorage';
import { headerData } from '../../data/headerCostum';

function Copyright(props) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
    >
      {"Copyright © "}
      <Link color="inherit" href="https://diskominfo.makassarkota.go.id/">
        Diskominfo Kota Makassar
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: '#1dc39f',
    },
    secondary: {
      main: '#8ff9ab',
    },
  },
  typography: {
    h5: {
      fontWeight: 600,
    },
  },
});

const DaftarLaporanLokasi = () => {
  const [longwis] = useLocalStorage("longwis");
  const reportTemplate = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dataLongwis, setDataLongwis] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch data longwis untuk preview
  const fetchLongwisData = useCallback(async () => {
    if (!longwis?.id) {
      setError('ID Longwis tidak ditemukan. Silakan pilih longwis terlebih dahulu.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}api/longwis/${longwis.id}`,
        {
          method: "GET",
          headers: headerData
        }
      );

      if (!response.ok) {
        throw new Error('Gagal mengambil data longwis');
      }

      const data = await response.json();
      
      if (data.nama_longwis) {
        setDataLongwis(data);
      } else {
        throw new Error('Data longwis tidak ditemukan');
      }
    } catch (err) {
      console.error('Error fetching longwis data:', err);
      setError(err.message || 'Terjadi kesalahan saat mengambil data');
    } finally {
      setLoading(false);
    }
  }, [longwis?.id]);

  // Load data saat komponen dimount
  useEffect(() => {
    fetchLongwisData();
  }, [fetchLongwisData]);

  // Generate PDF menggunakan html2pdf
  const handleDownloadPDF = async () => {
    if (!reportTemplate.current) {
      setError('Template laporan tidak ditemukan');
      return;
    }

    if (!dataLongwis) {
      setError('Data longwis belum dimuat. Silakan tunggu sebentar.');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setSuccess(null);

    try {
      // Konfigurasi PDF
      const opt = {
        margin: [15, 15, 15, 15], // margin dalam mm
        filename: `Laporan_Lokasi_${dataLongwis.nama_longwis?.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
          width: 794, // A4 width in pixels at 96 DPI
          height: 1123 // A4 height in pixels at 96 DPI
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait'
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      // Generate PDF dari element
      await html2pdf().set(opt).from(reportTemplate.current).save();
      
      setSuccess('PDF berhasil didownload!');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Gagal menggenerate PDF. Silakan coba lagi.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Alternative: Generate PDF dengan membuka di tab baru
  const handleOpenPDFTab = () => {
    if (!dataLongwis) {
      setError('Data longwis belum dimuat. Silakan tunggu sebentar.');
      return;
    }
    
    setIsGenerating(true);
    setTimeout(() => {
      window.open("/#/pic/download/lokasi", "_blank");
      setIsGenerating(false);
    }, 1000);
  };

  // Print langsung
  const handlePrint = () => {
    if (!dataLongwis) {
      setError('Data longwis belum dimuat. Silakan tunggu sebentar.');
      return;
    }

    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setIsPrinting(false);
    }, 500);
  };

  // Scroll ke preview
  const handlePreview = () => {
    if (reportTemplate.current) {
      reportTemplate.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #e8f5e8 0%, #f0fff0 100%)",
        py: 4
      }}
    >
      <ThemeProvider theme={defaultTheme}>
        <Container component="main" maxWidth="lg">
          <CssBaseline />
          
          <Paper 
            elevation={8}
            sx={{
              p: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.8) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}
          >
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  mb: 2,
                  boxShadow: 3
                }}
              >
                <DescriptionIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              
              <Typography component="h1" variant="h4" sx={{ mb: 1, color: 'primary.main', fontWeight: 700 }}>
                Download Laporan Lokasi
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', textAlign: 'center' }}>
                Preview dan download laporan lokasi Longwis dalam format PDF
              </Typography>

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                  {success}
                </Alert>
              )}

              {/* Loading Alert */}
              {loading && (
                <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={20} />
                    Memuat data longwis...
                  </Box>
                </Alert>
              )}
            </Box>

            <Grid container spacing={4}>
              {/* Preview Section */}
              <Grid item xs={12} lg={8}>
                <Card sx={{ height: '100%', border: '1px solid', borderColor: 'primary.light', borderRadius: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PreviewIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6" fontWeight={600}>Preview Laporan</Typography>
                    </Box>
                    
                    <Box 
                      ref={reportTemplate}
                      sx={{ 
                        border: '2px dashed', 
                        borderColor: 'primary.light',
                        borderRadius: 2,
                        p: 2,
                        backgroundColor: 'grey.50',
                        maxHeight: '70vh',
                        overflow: 'auto'
                      }}
                    >
                      <PrintPDFLokasi />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Action Section */}
              <Grid item xs={12} lg={4}>
                <Card sx={{ height: '100%', border: '1px solid', borderColor: 'primary.light', borderRadius: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <PictureAsPdfIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6" fontWeight={600}>Aksi Laporan</Typography>
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Primary PDF Download */}
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                        onClick={handleDownloadPDF}
                        disabled={isGenerating || isPrinting || loading || !dataLongwis}
                        sx={{
                          py: 1.5,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 600,
                          boxShadow: 3,
                          '&:hover': {
                            boxShadow: 6,
                          },
                          '&:disabled': {
                            opacity: 0.6
                          }
                        }}
                      >
                        {isGenerating ? "Menggenerate PDF..." : loading ? "Memuat Data..." : "Download PDF"}
                      </Button>

                      {/* Print Button */}
                      <Button
                        fullWidth
                        variant="outlined"
                        size="large"
                        startIcon={isPrinting ? <CircularProgress size={20} color="inherit" /> : <PrintIcon />}
                        onClick={handlePrint}
                        disabled={isGenerating || isPrinting || loading || !dataLongwis}
                        sx={{
                          py: 1.5,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 600,
                          borderWidth: 2,
                          '&:hover': {
                            borderWidth: 2,
                            backgroundColor: 'primary.main',
                            color: 'white'
                          },
                          '&:disabled': {
                            opacity: 0.6
                          }
                        }}
                      >
                        {isPrinting ? "Mempersiapkan..." : "Print Laporan"}
                      </Button>

                      {/* Preview Button */}
                      <Button
                        fullWidth
                        variant="text"
                        size="large"
                        startIcon={<PreviewIcon />}
                        onClick={handlePreview}
                        disabled={isGenerating || isPrinting || loading || !dataLongwis}
                        sx={{
                          py: 1.5,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 600,
                          color: 'primary.main',
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            color: 'white'
                          },
                          '&:disabled': {
                            opacity: 0.6
                          }
                        }}
                      >
                        Lihat Preview
                      </Button>

                      {/* Alternative: Open in New Tab */}
                      <Button
                        fullWidth
                        variant="outlined"
                        size="medium"
                        color="secondary"
                        onClick={handleOpenPDFTab}
                        disabled={isGenerating || isPrinting || loading || !dataLongwis}
                        sx={{
                          py: 1,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 500,
                          mt: 2,
                          borderColor: 'secondary.main',
                          color: 'secondary.main',
                          '&:hover': {
                            backgroundColor: 'secondary.main',
                            color: 'white'
                          },
                          '&:disabled': {
                            opacity: 0.6
                          }
                        }}
                      >
                        Buka di Tab Baru
                      </Button>
                    </Box>

                    {/* Report Details */}
                    <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.100', borderRadius: 2 }}>
                      <Typography variant="subtitle2" gutterBottom color="primary.main" fontWeight={600}>
                        Detail Laporan:
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Longwis:</strong> {dataLongwis?.nama_longwis || 'Memuat...'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>PIC:</strong> {dataLongwis?.nama_pic || 'Memuat...'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Tanggal:</strong> {new Date().toLocaleDateString('id-ID')}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Jenis:</strong> Laporan Lokasi Longwis
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Format:</strong> PDF (A4)
                      </Typography>
                      <Typography variant="body2">
                        <strong>Status:</strong> {
                          loading ? 'Memuat data...' :
                          isGenerating ? 'Sedang diproses...' : 
                          dataLongwis ? 'Siap untuk download' : 'Data tidak tersedia'
                        }
                      </Typography>
                    </Box>

                    {/* PDF Generation Info */}
                    {isGenerating && (
                      <Box sx={{ mt: 2, p: 2, backgroundColor: 'primary.light', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ color: 'white', textAlign: 'center' }}>
                          <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                          Mengkonversi laporan ke PDF...
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <Copyright sx={{ mt: 4 }} />
          </Paper>
        </Container>
      </ThemeProvider>
    </Box>
  );
};

export default DaftarLaporanLokasi;
